"use client";
import React from "react";
import { Box, Container, Typography, Card, CardContent } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import Image from "next/image";
import { IMAGES } from "../constants/images";
import { useTranslation } from "react-i18next";

const theme = createTheme({
  palette: {
    primary: { main: "#8b5cf6" },
    background: { default: "#faf5ff" },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
});

const AIRecruiterLanding = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const featuresRaw = t("aiRecruiterLanding.features", { returnObjects: true });
  const features = Array.isArray(featuresRaw) ? featuresRaw : [];

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: { xs: "100vh", sm: "auto", md: "100vh" },
          py: 8,
          position: "relative",
          overflow: "hidden",
          mb: { xs: "5%", sm: "-16%", md: "5%" },
          direction: isArabic ? "rtl" : "ltr",
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              textAlign: { xs: isArabic ? "right" : "left", lg: "center" },
              mb: 4,
              bgcolor: "#FAF6FC",
              borderRadius: "30px",
              height: {
                xs: "auto",
                sm: isArabic ? 750 : 820,
                md: isArabic ? 880 : 950,
                lg: isArabic ? 820 : 890,
                xl: isArabic ? 850 : 950,
              },
              px: { xs: 2, sm: 3, md: 4, lg: 0 },
            }}
          >
            {/* Badge */}
            <Box
              sx={{
                display: "inline-block",
                px: 2,
                py: 1.2,
                borderRadius: "22px",
                bgcolor: "transparent",
                fontSize: "0.9rem",
                color: "#9C9FAD",
                border: "1px solid #9C9FAD",
                mb: 4,
                mt: "4%",
                fontFamily: "var(--font-outfit)",
                fontWeight: 400,
              }}
            >
              {t("aiRecruiterLanding.badge")}
            </Box>

            {/* Heading */}
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: "bold",
                mb: 3.3,
                fontSize: {
                  xs: "1.8rem",
                  sm: "2.2rem",
                  md: "2.5rem",
                  lg: "2.5rem",
                  xl: "3rem",
                },
                maxWidth: "1000px",
                color: "#000000",
                mx: "auto",
                fontFamily: "var(--font-plusjakarta)",
                textAlign: { xs: isArabic ? "right" : "left", lg: "center" },
              }}
            >
              {t("aiRecruiterLanding.heading1")}{" "}
              <Box component="span" sx={{ color: "#6002EB" }}>
                {t("aiRecruiterLanding.headingHighlight1")}{" "}
              </Box>
              {t("aiRecruiterLanding.heading2")}{" "}
              <Box component="span" sx={{ color: "#6002EB" }}>
                {t("aiRecruiterLanding.headingHighlight2")}{" "}
              </Box>
              {t("aiRecruiterLanding.heading3")}
            </Typography>

            {/* Desc 1 */}
            <Typography
              sx={{
                maxWidth: { xs: "100%", sm: "100%", md: "700px", lg: "700px" },
                mx: { xs: 0, sm: 0, md: "auto" },
                mb: 3,
                color: "#02061799",
                lineHeight: 1.6,
                fontFamily: "var(--font-outfit)",
                fontWeight: 400,
                fontSize: {
                  xs: "16px",
                  sm: "17px",
                  md: "17px",
                  lg: "17px",
                  xl: "19px",
                },
                textAlign: { xs: isArabic ? "right" : "left", lg: "center" },
              }}
            >
              {t("aiRecruiterLanding.desc1")}
            </Typography>

            {/* Desc 2 */}
            <Typography
              sx={{
                maxWidth: { xs: "100%", sm: "100%", md: "700px", lg: "700px" },
                mx: { xs: 0, sm: 0, md: "auto" },
                mb: 4,
                color: "#02061799",
                lineHeight: 1.6,
                fontFamily: "var(--font-outfit)",
                fontWeight: 400,
                fontSize: {
                  xs: "16px",
                  sm: "17px",
                  md: "17px",
                  lg: "17px",
                  xl: "19px",
                },
                textAlign: { xs: isArabic ? "right" : "left", lg: "center" },
              }}
            >
              {t("aiRecruiterLanding.desc2")}
            </Typography>

            {/* Image & Cards */}
            <Box
              sx={{
                position: "relative",
                maxWidth: "1200px",
                mx: "auto",
                mb: 6,
              }}
            >
              {/* Background Image */}
              <Box
                sx={{
                  position: "relative",
                  width: { xs: "100%", sm: "90%", md: "70%" },
                  height: { xs: "250px", sm: "300px", md: "400px" },
                  mb: 2,
                  mx: "auto",
                  display: { xs: "none", sm: "none", md: "flex" },
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Image
                  src={IMAGES.AI_RECRUITER_LANDING_BG}
                  alt="AI recruiting software performance metrics background"
                  fill
                  style={{ objectFit: "contain", objectPosition: "center" }}
                  priority
                />
              </Box>

              {/* Feature Cards */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: {
                    xs: isArabic ? "flex-end" : "flex-start",
                    md: "center",
                  },
                  gap: 3,
                  flexWrap: "wrap",
                  mt: { xs: 3, sm: 4, md: -12 },
                }}
              >
                {features.map((feature, index) => (
                  <Card
                    key={index}
                    sx={{
                      flex: { xs: "1 1 100%", sm: "1 1 45%", md: "1 1 30%" },
                      maxWidth: "360px",
                      bgcolor: "#F0E5FF",
                      backdropFilter: "blur(10px)",
                      borderRadius: "16px",
                      border: "1px solid #F0E5FF",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      "&:hover": { transform: "translateY(-8px)" },
                      minHeight: { xs: "200px", sm: "auto" },
                      boxShadow: "none",
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          mb: 1.5,
                          color: "#9048FB",
                          fontSize: "1.4rem",
                          fontFamily: "var(--font-outfit)",
                          textAlign: isArabic ? "right" : "left",
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#02061799",
                          lineHeight: 1.3,
                          fontSize: "1rem",
                          fontFamily: "var(--font-outfit)",
                          textAlign: isArabic ? "right" : "left",
                          fontWeight: 400,
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default AIRecruiterLanding;
